/**
 * EditarDFDPage — edição do DFD gerado (F5e).
 *
 * Réplica do edit_dfd_v2.html: identificação, modalidade (Art. 28) e as 5
 * seções em textareas com o markdown cru. Salvar devolve o documento já
 * atualizado, então a tela de visualização não precisa refazer a consulta.
 */

import {
  faArrowLeft,
  faBalanceScale,
  faExclamationTriangle,
  faFileAlt,
  faIdCard,
  faSave,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  configuracaoDFD,
  formularioDFD,
  salvarDFD,
  type FormularioDFDGerado,
} from '../../api/dfd'
import { useToast } from '../../components/Toast'

const TITULOS_SECAO: Record<string, string> = {
  justificativa: '1. Justificativa',
  quantitativo: '2. Do Quantitativo',
  previsao_disponibilidade: '3. Previsão de Disponibilidade',
  demonstrativo_pca: '4. Demonstrativo do Plano de Contratações Anual (PCA)',
  encaminhamento: '5. Encaminhamento',
}

const CAMPOS_IDENTIFICACAO: [keyof FormularioDFDGerado, string, boolean][] = [
  ['objeto_contratacao', 'Objeto da Contratação', true],
  ['orgao_solicitante', 'Órgão Solicitante', false],
  ['numero_processo', 'Número do Processo', false],
  ['municipio', 'Município', false],
  ['responsavel', 'Servidor Responsável', false],
  ['cargo', 'Cargo', false],
  ['cpf_servidor', 'CPF do Servidor', false],
  ['matricula_servidor', 'Matrícula', false],
]

export function EditarDFDPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [form, setForm] = useState<FormularioDFDGerado | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['dfd-formulario', idNumerico],
    queryFn: () => formularioDFD(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })
  const { data: configuracao } = useQuery({
    queryKey: ['dfd-configuracao'],
    queryFn: configuracaoDFD,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const salvar = useMutation({
    mutationFn: () => {
      const f = form!
      return salvarDFD(idNumerico, {
        id: f.id, // ignorado pelo backend: quem manda é o id da URL
        objeto_contratacao: f.objeto_contratacao,
        orgao_solicitante: f.orgao_solicitante,
        numero_processo: f.numero_processo,
        municipio: f.municipio,
        responsavel: f.responsavel,
        cargo: f.cargo,
        cpf_servidor: f.cpf_servidor,
        matricula_servidor: f.matricula_servidor,
        tipo_objeto: f.tipo_objeto,
        modalidade_licitacao: f.modalidade_licitacao,
        usar_sistema_registro_precos: f.usar_sistema_registro_precos,
        justificativa_modalidade: f.justificativa_modalidade,
        secoes: f.secoes,
      })
    },
    onSuccess: (documento) => {
      clienteQuery.setQueryData(['dfd-documento', idNumerico], documento)
      void clienteQuery.invalidateQueries({ queryKey: ['processos-licitatorios'] })
      notificar('success', 'DFD atualizado com sucesso!')
      navegar(`/dfd/${idNumerico}`)
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando DFD…
      </p>
    )
  }
  if (error || !form) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Não foi possível abrir a edição
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ??
            'Este DFD não está no formato editável (v2.0).'}
        </p>
        <Link
          to={`/dfd/${idNumerico}`}
          className="inline-flex items-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white hover:bg-slate-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar ao documento
        </Link>
      </div>
    )
  }

  const definir = (campo: keyof FormularioDFDGerado, valor: string | boolean) =>
    setForm((atual) => (atual ? { ...atual, [campo]: valor } : atual))
  const definirSecao = (chave: string, valor: string) =>
    setForm((atual) =>
      atual ? { ...atual, secoes: { ...atual.secoes, [chave]: valor } } : atual,
    )

  const rotuloEntrada =
    'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300'
  const entrada =
    'w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

  return (
    <form
      className="mx-auto max-w-5xl"
      onSubmit={(e) => {
        e.preventDefault()
        salvar.mutate()
      }}
    >
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faFileAlt} className="mr-3 text-green-600" />
          Editar DFD
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Ajuste os dados e o texto das seções. Lei 14.133/2021.
        </p>
      </header>

      <fieldset className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <legend className="px-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faIdCard} className="mr-2 text-green-600" />
          Identificação
        </legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {CAMPOS_IDENTIFICACAO.map(([campo, rotulo, obrigatorio]) => (
            <div key={campo}>
              <label htmlFor={campo} className={rotuloEntrada}>
                {rotulo}
                {obrigatorio && <span className="ml-1 text-red-500">*</span>}
              </label>
              <input
                id={campo}
                name={campo}
                type="text"
                required={obrigatorio}
                value={String(form[campo] ?? '')}
                onChange={(e) => definir(campo, e.target.value)}
                className={entrada}
              />
            </div>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-6 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 shadow-lg dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20">
        <legend className="px-2 text-lg font-semibold text-indigo-800 dark:text-indigo-300">
          <FontAwesomeIcon icon={faBalanceScale} className="mr-2" />
          Modalidade de Licitação (Art. 28)
        </legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="tipo_objeto" className={rotuloEntrada}>
              Tipo de Objeto
            </label>
            <select
              id="tipo_objeto"
              name="tipo_objeto"
              value={form.tipo_objeto}
              onChange={(e) => definir('tipo_objeto', e.target.value)}
              className={entrada}
            >
              {(configuracao?.tipos_objeto ?? []).map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.rotulo}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="modalidade_licitacao" className={rotuloEntrada}>
              Modalidade
            </label>
            <select
              id="modalidade_licitacao"
              name="modalidade_licitacao"
              value={form.modalidade_licitacao}
              onChange={(e) => definir('modalidade_licitacao', e.target.value)}
              className={entrada}
            >
              {Object.entries(configuracao?.modalidades ?? {}).map(([valor, m]) => (
                <option key={valor} value={valor}>
                  {m.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            name="usar_sistema_registro_precos"
            checked={form.usar_sistema_registro_precos}
            onChange={(e) => definir('usar_sistema_registro_precos', e.target.checked)}
            className="h-4 w-4 accent-emerald-600"
          />
          Sistema de Registro de Preços (SRP/ARP — Art. 82 a 86)
        </label>

        <div className="mt-4">
          <label htmlFor="justificativa_modalidade" className={rotuloEntrada}>
            Justificativa para Modalidade Alternativa
          </label>
          <textarea
            id="justificativa_modalidade"
            name="justificativa_modalidade"
            rows={2}
            value={form.justificativa_modalidade}
            onChange={(e) => definir('justificativa_modalidade', e.target.value)}
            className={entrada}
          />
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Preencher aqui marca a modalidade como alternativa à principal do
            tipo de objeto.
          </p>
        </div>
      </fieldset>

      {Object.entries(TITULOS_SECAO).map(([chave, titulo]) => (
        <fieldset
          key={chave}
          className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card"
        >
          <legend className="px-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
            {titulo}
          </legend>
          <label htmlFor={`secao_${chave}`} className="sr-only">
            {titulo}
          </label>
          <textarea
            id={`secao_${chave}`}
            name={`secao_${chave}`}
            rows={14}
            value={form.secoes[chave] ?? ''}
            onChange={(e) => definirSecao(chave, e.target.value)}
            className={`${entrada} font-mono text-sm leading-relaxed`}
          />
        </fieldset>
      ))}

      <div className="flex flex-col justify-end gap-3 pb-4 sm:flex-row">
        <Link
          to={`/dfd/${idNumerico}`}
          className="inline-flex items-center justify-center rounded-xl bg-slate-200 px-6 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-300 dark:bg-dark-hover dark:text-slate-200"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Cancelar
        </Link>
        <button
          type="submit"
          disabled={salvar.isPending}
          className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-green-700 hover:to-emerald-700 disabled:opacity-60"
        >
          <FontAwesomeIcon
            icon={salvar.isPending ? faSpinner : faSave}
            spin={salvar.isPending}
            className="mr-2"
          />
          Salvar Alterações
        </button>
      </div>
    </form>
  )
}
